import { inject, Injectable, signal } from '@angular/core';
import { THEME_TOKEN } from '@porsche-design-system/components-angular';

export type ThemePreference = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'aspire-cockpit-theme';
const DEFAULT_THEME: ThemePreference = 'auto';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly pdsTheme$ = inject(THEME_TOKEN);
  readonly currentTheme = signal<ThemePreference>(this.readStoredTheme());

  constructor() {
    this.syncPdsTheme(this.currentTheme());
    this.applyTheme(this.currentTheme());

    const mediaQuery = this.getMediaQuery();
    if (!mediaQuery) {
      return;
    }

    const handleSystemThemeChange = () => {
      if (this.currentTheme() !== 'auto') {
        return;
      }

      this.applyTheme(this.currentTheme());
      this.syncPdsTheme(this.currentTheme());
    };

    if ('addEventListener' in mediaQuery) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
      return;
    }

    const legacyMediaQuery = mediaQuery as MediaQueryList & {
      addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
    };
    if (legacyMediaQuery.addListener) {
      legacyMediaQuery.addListener(handleSystemThemeChange as (event: MediaQueryListEvent) => void);
    }
  }

  setTheme(theme: ThemePreference): void {
    this.currentTheme.set(theme);
    this.persistTheme(theme);
    this.applyTheme(theme);
    this.syncPdsTheme(theme);
  }

  private readStoredTheme(): ThemePreference {
    if (typeof localStorage === 'undefined') {
      return DEFAULT_THEME;
    }

    const storedTheme = localStorage.getItem(STORAGE_KEY);
    if (storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'auto') {
      return storedTheme;
    }

    if (storedTheme === 'system') {
      return 'auto';
    }

    return DEFAULT_THEME;
  }

  private persistTheme(theme: ThemePreference): void {
    if (typeof localStorage === 'undefined') {
      return;
    }

    localStorage.setItem(STORAGE_KEY, theme);
  }

  private applyTheme(theme: ThemePreference): void {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const resolvedTheme = this.resolveTheme(theme);

    root.classList.remove('scheme-light', 'scheme-dark');
    root.classList.add(resolvedTheme === 'dark' ? 'scheme-dark' : 'scheme-light');
    root.setAttribute('data-theme', resolvedTheme);
    root.setAttribute('theme', theme);
    root.style.colorScheme = resolvedTheme;
  }

  private syncPdsTheme(theme: ThemePreference): void {
    this.pdsTheme$.next(theme);
  }

  private resolveTheme(theme: ThemePreference): 'light' | 'dark' {
    if (theme === 'light') {
      return 'light';
    }

    if (theme === 'dark') {
      return 'dark';
    }

    return this.getMediaQuery()?.matches ? 'dark' : 'light';
  }

  private getMediaQuery(): MediaQueryList | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return null;
    }

    return window.matchMedia('(prefers-color-scheme: dark)');
  }
}
